import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './AccountSettings.css';
import { getAuthToken, getApiBaseUrl } from '../../services/api';

const AccountSettings = () => {
  const { t } = useTranslation('user'); 
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    weeklyDigest: true,
    securityAlerts: true,
    activityReports: false
  });

  const [privacySettings, setPrivacySettings] = useState({
    profileVisibility: 'public',
    showEmail: false,
    showPhone: false,
    allowMessages: true
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteForm, setDeleteForm] = useState({
    password: '',
    confirmPassword: ''
  });
  const [deleteError, setDeleteError] = useState(null);
  const [deletingAccount, setDeletingAccount] = useState(false);

  const API_BASE_URL = getApiBaseUrl();

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNotificationChange = (e) => {
    const { name, checked } = e.target;
    setNotificationSettings(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handlePrivacyChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPrivacySettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    // Validation
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError(t('user.accountSettings.changePassword.passwordMismatch'));
      setLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setError(t('user.accountSettings.changePassword.passwordTooShort'));
      setLoading(false);
      return;
    }

    try {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required. Please login again.');

      const response = await fetch(`${API_BASE_URL}/api/user-settings/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        const errorData = contentType.includes('application/json') ? await response.json() : null;
        throw new Error(errorData?.error || errorData?.message || 'Failed to change password');
      }

      setSuccess(t('user.accountSettings.changePassword.passwordChanged'));
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      console.error('Error changing password:', err);
      setError(err.message || t('user.accountSettings.changePassword.passwordChangeFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = getAuthToken();
      if (!token) throw new Error('Authentication required. Please login again.');

      const response = await fetch(`${API_BASE_URL}/api/user-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...notificationSettings,
          ...privacySettings
        })
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        const errorData = contentType.includes('application/json') ? await response.json() : null;
        throw new Error(errorData?.error || errorData?.message || 'Failed to save settings');
      }

      setSuccess(t('user.accountSettings.notificationPreferences.settingsSaved'));
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.message || t('user.accountSettings.notificationPreferences.settingsSaveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = () => {
    setDeleteForm({ password: '', confirmPassword: '' });
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (deletingAccount) return;
    setShowDeleteModal(false);
    setDeleteForm({ password: '', confirmPassword: '' });
    setDeleteError(null);
  };

  const handleDeleteFormChange = (e) => {
    const { name, value } = e.target;
    setDeleteForm((prev) => ({ ...prev, [name]: value }));
    setDeleteError(null);
  };

  const clearSessionAndLeave = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('refreshToken');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('adminUser');
    sessionStorage.removeItem('syllera-user-dashboard-section');
    sessionStorage.removeItem('syllera-parent-dashboard-section');
    window.location.href = '/';
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    setDeleteError(null);

    if (!deleteForm.password || !deleteForm.confirmPassword) {
      setDeleteError(t('user.accountSettings.dangerZone.deleteAccount.passwordRequired'));
      return;
    }

    if (deleteForm.password !== deleteForm.confirmPassword) {
      setDeleteError(t('user.accountSettings.dangerZone.deleteAccount.passwordMismatch'));
      return;
    }

    setDeletingAccount(true);

    try {
      const token = getAuthToken();
      if (!token) throw new Error(t('user.accountSettings.dangerZone.deleteAccount.authRequired'));

      const response = await fetch(`${API_BASE_URL}/api/user-settings/account`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          password: deleteForm.password,
          confirmPassword: deleteForm.confirmPassword
        })
      });

      const contentType = response.headers.get('content-type') || '';
      const data = contentType.includes('application/json') ? await response.json() : null;

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error(t('user.accountSettings.dangerZone.deleteAccount.incorrectPassword'));
        }
        throw new Error(
          data?.message || data?.error || t('user.accountSettings.dangerZone.deleteAccount.failed')
        );
      }

      clearSessionAndLeave();
    } catch (err) {
      console.error('Error deleting account:', err);
      setDeleteError(err.message || t('user.accountSettings.dangerZone.deleteAccount.failed'));
      setDeletingAccount(false);
    }
  };

  return (
    <div className="account-settings">
      <div className="account-settings-header">
        <h2>{t('user.accountSettings.title')}</h2>
        <p>{t('user.accountSettings.subtitle')}</p>
      </div>

      {error && (
        <div className="account-error-message">
          <i className="material-icons">error</i>
          {error}
        </div>
      )}

      {success && (
        <div className="account-success-message">
          <i className="material-icons">check_circle</i>
          {success}
        </div>
      )}

      <div className="account-settings-content">
        {/* Change Password Section */}
        <div className="account-section">
          <h3>{t('user.accountSettings.changePassword.title')}</h3>
          <form onSubmit={handlePasswordSubmit} className="password-form">
            <div className="form-group">
              <label htmlFor="currentPassword">{t('user.accountSettings.changePassword.currentPassword')}</label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                required
                placeholder={t('user.accountSettings.changePassword.currentPasswordPlaceholder')}
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">{t('user.accountSettings.changePassword.newPassword')}</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                required
                placeholder={t('user.accountSettings.changePassword.newPasswordPlaceholder')}
                minLength={8}
              />
              <span className="field-hint">{t('user.accountSettings.changePassword.passwordHint')}</span>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">{t('user.accountSettings.changePassword.confirmPassword')}</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                required
                placeholder={t('user.accountSettings.changePassword.confirmPasswordPlaceholder')}
              />
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="save-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <i className="material-icons rotating">refresh</i>
                    {t('user.accountSettings.changePassword.changingPassword')}
                  </>
                ) : (
                  <>
                    <i className="material-icons">lock</i>
                    {t('user.accountSettings.changePassword.changePassword')}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Notification Settings Section */}
        <div className="account-section">
          <h3>{t('user.accountSettings.notificationPreferences.title')}</h3>
          <div className="settings-form">
            <div className="setting-item">
              <div className="setting-info">
                <label htmlFor="emailNotifications">{t('user.accountSettings.notificationPreferences.emailNotifications.label')}</label>
                <p>{t('user.accountSettings.notificationPreferences.emailNotifications.description')}</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  id="emailNotifications"
                  name="emailNotifications"
                  checked={notificationSettings.emailNotifications}
                  onChange={handleNotificationChange}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <label htmlFor="pushNotifications">{t('user.accountSettings.notificationPreferences.pushNotifications.label')}</label>
                <p>{t('user.accountSettings.notificationPreferences.pushNotifications.description')}</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  id="pushNotifications"
                  name="pushNotifications"
                  checked={notificationSettings.pushNotifications}
                  onChange={handleNotificationChange}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <label htmlFor="smsNotifications">{t('user.accountSettings.notificationPreferences.smsNotifications.label')}</label>
                <p>{t('user.accountSettings.notificationPreferences.smsNotifications.description')}</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  id="smsNotifications"
                  name="smsNotifications"
                  checked={notificationSettings.smsNotifications}
                  onChange={handleNotificationChange}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <label htmlFor="weeklyDigest">{t('user.accountSettings.notificationPreferences.weeklyDigest.label')}</label>
                <p>{t('user.accountSettings.notificationPreferences.weeklyDigest.description')}</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  id="weeklyDigest"
                  name="weeklyDigest"
                  checked={notificationSettings.weeklyDigest}
                  onChange={handleNotificationChange}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <label htmlFor="securityAlerts">{t('user.accountSettings.notificationPreferences.securityAlerts.label')}</label>
                <p>{t('user.accountSettings.notificationPreferences.securityAlerts.description')}</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  id="securityAlerts"
                  name="securityAlerts"
                  checked={notificationSettings.securityAlerts}
                  onChange={handleNotificationChange}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <label htmlFor="activityReports">{t('user.accountSettings.notificationPreferences.activityReports.label')}</label>
                <p>{t('user.accountSettings.notificationPreferences.activityReports.description')}</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  id="activityReports"
                  name="activityReports"
                  checked={notificationSettings.activityReports}
                  onChange={handleNotificationChange}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="save-btn"
                onClick={handleNotificationSave}
                disabled={loading}
              >
                <i className="material-icons">save</i>
                {t('user.accountSettings.notificationPreferences.saveSettings')}
              </button>
            </div>
          </div>
        </div>

        {/* Privacy Settings Section */}
        <div className="account-section">
          <h3>{t('user.accountSettings.privacySettings.title')}</h3>
          <div className="settings-form">
            <div className="form-group">
              <label htmlFor="profileVisibility">{t('user.accountSettings.privacySettings.profileVisibility.label')}</label>
              <select
                id="profileVisibility"
                name="profileVisibility"
                value={privacySettings.profileVisibility}
                onChange={handlePrivacyChange}
              >
                <option value="public">{t('user.accountSettings.privacySettings.profileVisibility.public')}</option>
                <option value="private">{t('user.accountSettings.privacySettings.profileVisibility.private')}</option>
                <option value="contacts">{t('user.accountSettings.privacySettings.profileVisibility.contactsOnly')}</option>
              </select>
              <span className="field-hint">{t('user.accountSettings.privacySettings.profileVisibility.hint')}</span>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <label htmlFor="showEmail">{t('user.accountSettings.privacySettings.showEmail.label')}</label>
                <p>{t('user.accountSettings.privacySettings.showEmail.description')}</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  id="showEmail"
                  name="showEmail"
                  checked={privacySettings.showEmail}
                  onChange={handlePrivacyChange}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <label htmlFor="showPhone">{t('user.accountSettings.privacySettings.showPhone.label')}</label>
                <p>{t('user.accountSettings.privacySettings.showPhone.description')}</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  id="showPhone"
                  name="showPhone"
                  checked={privacySettings.showPhone}
                  onChange={handlePrivacyChange}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <label htmlFor="allowMessages">{t('user.accountSettings.privacySettings.allowMessages.label')}</label>
                <p>{t('user.accountSettings.privacySettings.allowMessages.description')}</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  id="allowMessages"
                  name="allowMessages"
                  checked={privacySettings.allowMessages}
                  onChange={handlePrivacyChange}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="save-btn"
                onClick={handleNotificationSave}
                disabled={loading}
              >
                <i className="material-icons">save</i>
                {t('user.accountSettings.privacySettings.saveSettings')}
              </button>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="account-section danger-zone">
          <h3>{t('user.accountSettings.dangerZone.title')}</h3>
          <div className="danger-actions">
            <div className="danger-item">
              <div className="danger-info">
                <h4>{t('user.accountSettings.dangerZone.deleteAccount.title')}</h4>
                <p>{t('user.accountSettings.dangerZone.deleteAccount.description')}</p>
              </div>
              <button type="button" className="danger-btn" onClick={openDeleteModal}>
                <i className="material-icons">delete</i>
                {t('user.accountSettings.dangerZone.deleteAccount.button')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <div className="account-modal-overlay" onClick={closeDeleteModal}>
          <div
            className="account-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-account-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="account-modal-close"
              onClick={closeDeleteModal}
              disabled={deletingAccount}
              aria-label={t('user.accountSettings.dangerZone.deleteAccount.cancel')}
            >
              ×
            </button>
            <div className="account-modal-icon">
              <i className="material-icons">warning</i>
            </div>
            <h2 id="delete-account-title">
              {t('user.accountSettings.dangerZone.deleteAccount.modalTitle')}
            </h2>
            <p>{t('user.accountSettings.dangerZone.deleteAccount.warning')}</p>

            <form onSubmit={handleDeleteAccount} className="delete-account-form">
              {deleteError && (
                <div className="account-error-message">
                  <i className="material-icons">error</i>
                  {deleteError}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="deletePassword">
                  {t('user.accountSettings.dangerZone.deleteAccount.password')}
                </label>
                <input
                  type="password"
                  id="deletePassword"
                  name="password"
                  value={deleteForm.password}
                  onChange={handleDeleteFormChange}
                  autoComplete="current-password"
                  disabled={deletingAccount}
                  required
                  placeholder={t('user.accountSettings.dangerZone.deleteAccount.passwordPlaceholder')}
                />
              </div>

              <div className="form-group">
                <label htmlFor="deleteConfirmPassword">
                  {t('user.accountSettings.dangerZone.deleteAccount.confirmPassword')}
                </label>
                <input
                  type="password"
                  id="deleteConfirmPassword"
                  name="confirmPassword"
                  value={deleteForm.confirmPassword}
                  onChange={handleDeleteFormChange}
                  autoComplete="new-password"
                  disabled={deletingAccount}
                  required
                  placeholder={t('user.accountSettings.dangerZone.deleteAccount.confirmPasswordPlaceholder')}
                />
              </div>

              <div className="account-modal-actions">
                <button
                  type="button"
                  className="account-modal-cancel"
                  onClick={closeDeleteModal}
                  disabled={deletingAccount}
                >
                  {t('user.accountSettings.dangerZone.deleteAccount.cancel')}
                </button>
                <button
                  type="submit"
                  className="danger-btn confirm-delete-btn"
                  disabled={deletingAccount}
                >
                  {deletingAccount ? (
                    <>
                      <i className="material-icons rotating">refresh</i>
                      {t('user.accountSettings.dangerZone.deleteAccount.deleting')}
                    </>
                  ) : (
                    <>
                      <i className="material-icons">delete_forever</i>
                      {t('user.accountSettings.dangerZone.deleteAccount.confirmButton')}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSettings;

